import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getStoredSession } from '../hooks/useAuth';
import {
  getDrillArchive, fetchDrillSetQuestionsById,
  type DrillArchiveEntry, type DrillQuestion,
} from '../services/drillService';
import ChinniExplainer from '../components/ChinniExplainer';
import {
  ArrowLeft, Zap, RotateCcw, ChevronDown, ChevronUp,
  CheckCircle2, XCircle, Clock, FileText,
} from 'lucide-react';

// ── Helpers ────────────────────────────────────────────────────

function pct(entry: DrillArchiveEntry): number {
  if (!entry.questions_attempted) return 0;
  return Math.round((entry.questions_correct / entry.questions_attempted) * 100);
}

function scoreColor(p: number) {
  if (p >= 80) return 'text-green-600 bg-green-50 border-green-200';
  if (p >= 60) return 'text-amber-600 bg-amber-50 border-amber-200';
  return 'text-red-600 bg-red-50 border-red-200';
}

function scoreDot(p: number) {
  if (p >= 80) return 'bg-green-500';
  if (p >= 60) return 'bg-amber-400';
  return 'bg-red-400';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

const DIFF_LABEL: Record<string, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
const DIFF_COLOR: Record<string, string> = {
  easy: 'text-green-700 bg-green-100',
  medium: 'text-amber-700 bg-amber-100',
  hard: 'text-red-700 bg-red-100',
};
const MODE_LABEL: Record<string, string> = { rapid_fire: 'Rapid fire', relax: 'Relax' };
const OPTION_KEYS = ['option_a', 'option_b', 'option_c', 'option_d'] as const;
const OPTION_LABELS = ['A', 'B', 'C', 'D'];

// ── Group key ─────────────────────────────────────────────────

function groupKey(e: DrillArchiveEntry): string {
  return e.concept_id != null
    ? `concept::${e.concept_id}::${e.difficulty}`
    : `paste::${e.question_set_id}::${e.difficulty}`;
}

// ── Session detail (expandable) ───────────────────────────────

function SessionDetail({ entry }: { entry: DrillArchiveEntry }) {
  const [questions, setQuestions] = useState<DrillQuestion[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchDrillSetQuestionsById(entry.question_set_id).then(qs => {
      setQuestions(qs);
      setLoading(false);
    });
  }, [entry.question_set_id]);

  if (loading) return (
    <div className="flex items-center gap-2 py-4 text-xs text-gray-400">
      <div className="w-3 h-3 border border-gray-300 border-t-orange-400 rounded-full animate-spin" />
      Loading questions…
    </div>
  );

  const optionText = (q: DrillQuestion, idx: number | null) =>
    idx !== null ? `${OPTION_LABELS[idx]}. ${q[OPTION_KEYS[idx]]}` : '—';

  return (
    <div className="space-y-3 pt-3">
      {entry.results.map((r) => {
        const q = questions?.[r.question_index];
        if (!q) return (
          <div key={r.question_index} className="rounded-xl p-3 border border-gray-100 bg-gray-50 text-xs text-gray-400">
            Question {r.question_index + 1} — loading…
          </div>
        );
        return (
          <div key={r.question_index} className={`rounded-xl border overflow-hidden ${r.correct ? 'border-green-200' : 'border-red-200'}`}>
            <div className={`flex items-start gap-2 px-4 py-3 ${r.correct ? 'bg-green-50' : 'bg-red-50'}`}>
              {r.correct
                ? <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                : <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />}
              <p className="text-sm text-gray-800 font-medium leading-snug">{q.question}</p>
            </div>
            <div className="px-4 py-3 bg-white space-y-2">
              {!r.correct && r.student_answer !== null && (
                <div className="flex items-start gap-2 text-xs">
                  <span className="text-red-500 font-semibold shrink-0 mt-0.5">You chose:</span>
                  <span className="text-red-700">{optionText(q, r.student_answer)}</span>
                </div>
              )}
              <div className="flex items-start gap-2 text-xs">
                <span className="text-green-600 font-semibold shrink-0 mt-0.5">Correct:</span>
                <span className="text-green-800">{optionText(q, q.correct_option_index)}</span>
              </div>
              {r.timed_out && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Time ran out
                </p>
              )}
              {!r.correct && q.explanation && (
                <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2 leading-relaxed border border-gray-100">
                  {q.explanation}
                </p>
              )}
              <ChinniExplainer
                question={q.question}
                optionA={q[OPTION_KEYS[0]]} optionB={q[OPTION_KEYS[1]]}
                optionC={q[OPTION_KEYS[2]]} optionD={q[OPTION_KEYS[3]]}
                correctIndex={q.correct_option_index}
                conceptName={entry.concept_name ?? 'this concept'}
                gradeLevel={entry.grade}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Session card ──────────────────────────────────────────────

function SessionCard({
  entry, onRetake,
}: {
  entry: DrillArchiveEntry;
  onRetake: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const p = pct(entry);
  const wrong = entry.results.filter(r => !r.correct).length;

  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden bg-white">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold border shrink-0 ${scoreColor(p)}`}>
          {p}%
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-600 font-medium">
            {formatDate(entry.completed_at)} · {MODE_LABEL[entry.mode]}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {entry.questions_correct} correct · {wrong} wrong · {entry.questions_attempted} total
          </p>
        </div>
        <button
          onClick={onRetake}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-orange-600 bg-orange-50 hover:bg-orange-100 rounded-lg transition-colors shrink-0"
        >
          <RotateCcw className="w-3 h-3" /> Retake
        </button>
      </div>
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-center gap-1.5 py-2 border-t border-gray-100 text-xs font-semibold text-gray-500 hover:text-orange-600 hover:bg-orange-50 transition-colors"
      >
        {expanded
          ? <><ChevronUp className="w-3.5 h-3.5" /> Hide answers</>
          : <><ChevronDown className="w-3.5 h-3.5" /> Review answers</>}
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
          <SessionDetail entry={entry} />
        </div>
      )}
    </div>
  );
}

// ── Group card ────────────────────────────────────────────────

function GroupCard({
  entries, onRetake,
}: {
  entries: DrillArchiveEntry[];
  onRetake: (entry: DrillArchiveEntry) => void;
}) {
  const [open, setOpen] = useState(false);
  const first = entries[0];
  const isPaste = first.source_type === 'paste';
  const scores = entries.map(pct);
  const latest = scores[0];
  const oldest = scores[scores.length - 1];
  const trend = scores.length > 1 ? latest - oldest : null;

  return (
    <div className="glass-panel rounded-2xl overflow-hidden">
      {/* Group header */}
      <button
        className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-gray-50/50 transition-colors"
        onClick={() => setOpen(v => !v)}
      >
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center shrink-0">
          {isPaste ? <FileText className="w-5 h-5 text-white" /> : <Zap className="w-5 h-5 text-white" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-ocean-deep text-sm truncate">
              {isPaste ? 'Pasted Content' : (first.concept_name ?? 'Unknown')}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DIFF_COLOR[first.difficulty]}`}>
              {DIFF_LABEL[first.difficulty]}
            </span>
          </div>
          <div className="flex items-center gap-3 mt-1">
            {/* Score trend dots */}
            <div className="flex items-center gap-1">
              {[...scores].reverse().map((s, i) => (
                <div key={i} className={`w-2 h-2 rounded-full ${scoreDot(s)}`} title={`${s}%`} />
              ))}
            </div>
            {trend !== null && (
              <span className={`text-xs font-medium ${trend > 0 ? 'text-green-600' : trend < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                {trend > 0 ? `↑ +${trend}pp` : trend < 0 ? `↓ ${trend}pp` : '→ same'}
              </span>
            )}
            <span className="text-xs text-gray-400">{entries.length} session{entries.length > 1 ? 's' : ''}</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className={`px-3 py-1.5 rounded-xl border text-sm font-bold ${scoreColor(latest)}`}>
            {latest}%
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </button>

      {/* Sessions list */}
      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-gray-100">
          {entries.map(entry => (
            <SessionCard
              key={entry.session_id}
              entry={entry}
              onRetake={() => onRetake(entry)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────

export default function DrillArchive() {
  const session = getStoredSession();
  const navigate = useNavigate();
  const [entries, setEntries] = useState<DrillArchiveEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.student_id) return;
    getDrillArchive(session.student_id).then(data => {
      setEntries(data);
      setLoading(false);
    });
  }, [session?.student_id]);

  // Group by concept+difficulty
  const groups = entries.reduce((acc, entry) => {
    const key = groupKey(entry);
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {} as Record<string, DrillArchiveEntry[]>);

  const groupList = Object.values(groups);

  const totalSessions = entries.length;
  const avgScore = totalSessions > 0
    ? Math.round(entries.reduce((s, e) => s + pct(e), 0) / totalSessions)
    : 0;
  const bestScore = totalSessions > 0 ? Math.max(...entries.map(pct)) : 0;

  function handleRetake(entry: DrillArchiveEntry) {
    navigate('/drill', {
      state: {
        retakeSetId: entry.question_set_id,
        conceptId: entry.concept_id,
        conceptName: entry.concept_name,
        difficulty: entry.difficulty,
        sourceType: entry.source_type,
      },
    });
  }

  if (loading) {
    return (
      <main className="max-w-2xl mx-auto px-4 pt-24 pb-8 text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-400 mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading your drill history…</p>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto w-full px-4 sm:px-6 pt-24 pb-10">
      <Link to="/drill" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-ocean-deep mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Drill
      </Link>

      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-ocean-deep">Drill History</h1>
        <p className="text-gray-500 text-sm mt-1">
          {totalSessions} session{totalSessions !== 1 ? 's' : ''} across {groupList.length} concept{groupList.length !== 1 ? 's' : ''}
        </p>
      </div>

      {totalSessions > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="glass-panel rounded-xl p-3 text-center">
            <div className="text-xl font-extrabold text-ocean-deep">{totalSessions}</div>
            <div className="text-xs text-gray-500 mt-0.5">Sessions</div>
          </div>
          <div className="glass-panel rounded-xl p-3 text-center">
            <div className="text-xl font-extrabold text-ocean-deep">{avgScore}%</div>
            <div className="text-xs text-gray-500 mt-0.5">Avg score</div>
          </div>
          <div className="glass-panel rounded-xl p-3 text-center">
            <div className="text-xl font-extrabold text-ocean-deep">{bestScore}%</div>
            <div className="text-xs text-gray-500 mt-0.5">Best score</div>
          </div>
        </div>
      )}

      {groupList.length === 0 ? (
        <div className="glass-panel rounded-2xl p-10 text-center">
          <Zap className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No drill sessions yet</p>
          <p className="text-gray-400 text-sm mt-1 mb-5">Complete a drill to see your history here</p>
          <Link
            to="/drill"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-orange-400 to-orange-600 text-white rounded-xl text-sm font-bold shadow"
          >
            <Zap className="w-4 h-4" /> Start Drilling
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {groupList.map((group, i) => (
            <GroupCard key={i} entries={group} onRetake={handleRetake} />
          ))}
        </div>
      )}
    </main>
  );
}
